import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Pagination,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Content } from 'antd/lib/layout/layout';
import Sider from 'antd/lib/layout/Sider';
import type {
  FilterDropdownProps,
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import Text from 'antd/lib/typography/Text';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type React from 'react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { useDepartments } from '@/common/useDepartments';
import { StatusFilterSkeleton } from '@/components/skeletons/StatusFilterSkeleton';
import { useTableSkeleton } from '@/components/skeletons/useTableSkeleton';
import { type GetOrdersQuery, Order_By } from '@/generated/graphql/graphql';
import { OrderDrawerView } from '../OrderDrawer/OrderDrawerView';
import {
  useGetBlockedDrivers,
  useGetMeta,
  useGetOrders,
  useGetStatuses,
} from '../query/orders.hooks';
import { buildOrdersWhereForStatusCounts, buildWhereClause } from '../utils/buildWhereClause';
import { StatusFilterLinks } from './StatusFilterLinks';
import { UserEmail } from './UserEmail';

dayjs.extend(utc);

const BLOCKED_TAG_STYLE: React.CSSProperties = { marginLeft: 4, fontSize: '10px' };

const getDefaultCreatedAtFilter = (): [string, string] => {
  const now = dayjs.utc();
  const threeMonthsAgo = now.subtract(3, 'month');
  const startDate = dayjs.utc(`${threeMonthsAgo.format('YYYY-MM-DD')}T00:00:00`).toISOString();
  const endDate = dayjs.utc(`${now.format('YYYY-MM-DD')}T23:59:59.999`).toISOString();
  return [startDate, endDate];
};

const getDefaultFilters = (): FiltersType => ({
  status: 'New',
  searchText: '',
  products: [],
  dates: {
    plannedDate: null,
    deliveredDate: null,
    updatedAt: null,
    createdAt: getDefaultCreatedAtFilter(),
  },
  resale: null,
  isSaved: null,
  driverIDs: [],
  priceRange: null,
  showOnlyExpress: null,
  showBlockedDrivers: false,
});

const parseBooleanFilter = (filterValue: FilterValue | null): boolean | null => {
  if (!filterValue || !Array.isArray(filterValue) || filterValue[0] == null) {
    return null;
  }
  return filterValue[0] === 'true';
};

const parseArrayFilter = (filterValue: FilterValue | null): string[] => {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
    return [];
  }
  return filterValue as string[];
};

const parseDateRangeFilter = (filterValue: string | null): [string, string] | null => {
  if (!filterValue) return null;

  const dateValues = filterValue.split('|');
  if (dateValues.length === 2) {
    return [dateValues[0], dateValues[1]];
  }
  return null;
};

const parsePriceRangeFilter = (filterValue: string | null): [string, string] | null => {
  if (!filterValue) return null;

  const priceValues = filterValue.split('|');
  if (priceValues.length === 2) {
    return [priceValues[0], priceValues[1]];
  }
  return null;
};

type FiltersType = {
  status: string;
  searchText: string;
  products: string[];
  dates: {
    plannedDate: [string, string] | null;
    deliveredDate: [string, string] | null;
    updatedAt: [string, string] | null;
    createdAt: [string, string] | null;
  };
  resale: boolean | null;
  isSaved: boolean | null;
  driverIDs: string[];
  priceRange: [string, string] | null;
  showOnlyExpress: boolean | null;
  showBlockedDrivers: boolean;
};

type StoredFiltersType = Omit<FiltersType, 'dates'> & {
  dates: Omit<FiltersType['dates'], 'createdAt'>;
};

const loadFiltersFromStorage = (): FiltersType => {
  try {
    const stored = localStorage.getItem('orderTableFilters');
    if (stored) {
      const parsed = JSON.parse(stored) as StoredFiltersType;
      const defaultFilters = getDefaultFilters();
      return {
        ...defaultFilters,
        ...parsed,
        dates: {
          ...defaultFilters.dates,
          ...parsed.dates,
          createdAt: getDefaultCreatedAtFilter(),
        },
      };
    }
  } catch {}
  return getDefaultFilters();
};

const getInitialFilters = (): FiltersType => {
  const stored = loadFiltersFromStorage();
  if (typeof window === 'undefined') return stored;
  const params = new URLSearchParams(window.location.search);
  const statusFromUrl = params.get('statusName');
  if (statusFromUrl != null && statusFromUrl.trim() !== '') {
    return { ...stored, status: statusFromUrl.trim() };
  }
  return stored;
};

const saveFiltersToStorage = (filters: FiltersType): void => {
  try {
    const { dates, ...restFilters } = filters;
    const { createdAt: _createdAt, ...restDates } = dates;
    const filtersWithoutCreatedAt: StoredFiltersType = {
      ...restFilters,
      dates: restDates,
    };
    localStorage.setItem('orderTableFilters', JSON.stringify(filtersWithoutCreatedAt));
  } catch {}
};

export const OrderTableView = () => {
  const {
    departments,
    selectedDepartmentId: departmentId,
    selectedDepartment,
    setSelectedDepartmentId,
  } = useDepartments();

  const { data: meta } = useGetMeta({ departmentId });

  const [filters, setFiltersState] = useState<FiltersType>(getInitialFilters);

  const { data: blockedDriversData, isLoading: blockedDriversLoading } = useGetBlockedDrivers(
    { departmentId },
    { enabled: filters.showBlockedDrivers }
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const lastOrderIdFromUrlRef = useRef<number | null>(null);
  const skipUrlOrderIdRef = useRef<number | null>(null);

  const setFilters = useCallback((updater: FiltersType | ((prev: FiltersType) => FiltersType)) => {
    setFiltersState((prev) => {
      const newFilters = typeof updater === 'function' ? updater(prev) : updater;
      saveFiltersToStorage(newFilters);
      return newFilters;
    });
  }, []);

  const [sortOrder, setSortOrder] = useLocalStorage<{ field: string; order: Order_By } | null>(
    'orderTableSort',
    null
  );

  const [selectedOrders, setSelectedOrders] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('orderTableSelected');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id));
        }
      }
    } catch {}
    return [];
  });

  const persistSelectedOrders = useCallback((orders: number[]) => {
    try {
      localStorage.setItem('orderTableSelected', JSON.stringify(orders));
    } catch {}
  }, []);

  const parseNumberParam = useCallback((value: string | null): number | null => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const [searchValue, setSearchValue] = useState(filters.searchText);
  useEffect(() => {
    setSearchValue(filters.searchText);
  }, [filters.searchText]);

  const orderIdParam = parseNumberParam(searchParams.get('orderId'));
  const statusNameParam = searchParams.get('statusName');
  const statusFromUrl = (statusNameParam?.trim() ?? '') || null;

  const timezoneOffset = selectedDepartment?.timeZone ?? 0;

  const effectiveFiltersForWhere = useMemo(() => {
    if (statusFromUrl) {
      return { ...filters, status: statusFromUrl };
    }
    return filters;
  }, [filters, statusFromUrl]);

  const ordersWhereForStatusCounts = useMemo(
    () => buildOrdersWhereForStatusCounts(departmentId, effectiveFiltersForWhere, timezoneOffset),
    [departmentId, effectiveFiltersForWhere, timezoneOffset]
  );

  const { data: statuses } = useGetStatuses({ ordersWhere: ordersWhereForStatusCounts });

  useEffect(() => {
    const departmentParam = parseNumberParam(searchParams.get('departmentId'));
    if (!departmentParam) return;
    const departmentExists = departments.some((department) => department.id === departmentParam);
    if (!departmentExists || departmentParam === departmentId) return;
    setSelectedDepartmentId(departmentParam);
  }, [departments, departmentId, parseNumberParam, searchParams, setSelectedDepartmentId]);

  useEffect(() => {
    const trimmed = statusNameParam?.trim() ?? '';
    if (trimmed !== '') {
      setFilters((prev) => (prev.status === trimmed ? prev : { ...prev, status: trimmed }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [statusNameParam, setFilters]);

  useEffect(() => {
    if (orderIdParam) {
      if (skipUrlOrderIdRef.current === orderIdParam) {
        return;
      }
      if (selectedOrders.length !== 1 || selectedOrders[0] !== orderIdParam) {
        setSelectedOrders([orderIdParam]);
      }
      lastOrderIdFromUrlRef.current = orderIdParam;
      return;
    }

    if (
      lastOrderIdFromUrlRef.current !== null &&
      selectedOrders.length === 1 &&
      selectedOrders[0] === lastOrderIdFromUrlRef.current
    ) {
      setSelectedOrders([]);
      lastOrderIdFromUrlRef.current = null;
    }

    if (!orderIdParam) {
      skipUrlOrderIdRef.current = null;
    }
  }, [orderIdParam, selectedOrders]);

  useEffect(() => {
    if (filters.priceRange === undefined) {
      setFilters((prev) => ({
        ...prev,
        priceRange: null,
      }));
    }
  }, [filters, setFilters]);

  const whereClause = useMemo(
    () => buildWhereClause(departmentId, effectiveFiltersForWhere, timezoneOffset),
    [departmentId, effectiveFiltersForWhere, timezoneOffset]
  );

  const driverFilterList = useMemo(() => {
    return filters.showBlockedDrivers ? blockedDriversData?.user : meta?.user;
  }, [filters.showBlockedDrivers, blockedDriversData?.user, meta?.user]);

  const handleToggleBlockedDrivers = useCallback(
    (checked: boolean) => {
      setFilters((prev) => ({
        ...prev,
        showBlockedDrivers: checked,
        driverIDs: [],
      }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [setFilters]
  );

  const { data: orders, isLoading: ordersLoading } = useGetOrders({
    limit: pagination.pageSize,
    offset: (pagination.current - 1) * pagination.pageSize,
    where: whereClause,
    sorters: sortOrder ? { [sortOrder.field]: sortOrder.order } : { id: Order_By.Asc },
  });

  useEffect(() => {
    const total = orders?.order_aggregate?.aggregate?.count;
    if (typeof total === 'number') {
      setPagination((prev) => ({
        ...prev,
        total,
      }));
    }
  }, [orders]);

  const handleStatusChange = useCallback(
    (status: string) => {
      setFilters((prev) => ({ ...prev, status }));
      setPagination((prev) => ({ ...prev, current: 1 }));
      const nextParams = new URLSearchParams(searchParams);
      if (status === 'ALL') {
        nextParams.delete('statusName');
      } else {
        nextParams.set('statusName', status);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setFilters, setSearchParams]
  );

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const setOrderIdParam = useCallback(
    (orderId: number | null) => {
      const nextParams = new URLSearchParams(searchParams);
      if (!orderId) {
        nextParams.delete('orderId');
      } else {
        nextParams.set('orderId', String(orderId));
        nextParams.set('departmentId', String(departmentId));
      }
      setSearchParams(nextParams, { replace: true });
    },
    [departmentId, searchParams, setSearchParams]
  );

  const closeDrawer = useCallback(() => {
    setSelectedOrders([]);
    persistSelectedOrders([]);
    skipUrlOrderIdRef.current = orderIdParam ?? null;
    setOrderIdParam(null);
    lastOrderIdFromUrlRef.current = null;
  }, [orderIdParam, persistSelectedOrders, setOrderIdParam]);

  const updatePagination = useCallback((pagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize ?? prev.pageSize,
    }));
  }, []);

  const updateSorting = useCallback(
    (
      sorter:
        | SorterResult<GetOrdersQuery['order'][number]>
        | SorterResult<GetOrdersQuery['order'][number]>[]
    ) => {
      if (Array.isArray(sorter)) return;

      if (sorter?.field && sorter.order) {
        const order = sorter.order === 'ascend' ? Order_By.Asc : Order_By.Desc;
        setSortOrder({ field: String(sorter.field), order });
      } else {
        setSortOrder(null);
      }
    },
    [setSortOrder]
  );

  const applyFilters = useCallback(
    (tableFilters: Record<string, FilterValue | null>) => {
      const newFilters = { ...filters };

      newFilters.resale = parseBooleanFilter(tableFilters.resale);
      newFilters.isSaved = parseBooleanFilter(tableFilters.isSaved);
      newFilters.driverIDs = parseArrayFilter(tableFilters.driverID);
      newFilters.products = parseArrayFilter(tableFilters.firstPosition);

      const dateFields = ['plannedDate', 'deliveredDate', 'updatedAt', 'createdAt'] as const;
      dateFields.forEach((dateField) => {
        const filterValues = tableFilters[dateField] as string[] | null;
        const filterValue = filterValues?.[0] ? String(filterValues[0]) : null;
        newFilters.dates[dateField] = parseDateRangeFilter(filterValue);
      });

      const priceFilterValues = tableFilters.totalCents as string[] | null;
      const priceFilterValue = priceFilterValues?.[0] ? String(priceFilterValues[0]) : null;
      newFilters.priceRange = parsePriceRangeFilter(priceFilterValue);

      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<GetOrdersQuery['order'][number]>
      | SorterResult<GetOrdersQuery['order'][number]>[],
    _extra: TableCurrentDataSource<GetOrdersQuery['order'][number]>
  ) => {
    updatePagination(pagination);
    updateSorting(sorter);
    applyFilters(tableFilters);
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      ...getDefaultFilters(),
      status: prev.status,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSortOrder(null);
    setSelectedOrders([]);
    persistSelectedOrders([]);
    skipUrlOrderIdRef.current = orderIdParam ?? null;
    setOrderIdParam(null);
    lastOrderIdFromUrlRef.current = null;
  };

  const getColumnSortOrder = (field: string) =>
    sortOrder?.field === field
      ? sortOrder.order === Order_By.Asc
        ? 'ascend'
        : 'descend'
      : undefined;

  const getSortedColumnClassName = (field: string) =>
    sortOrder?.field === field ? 'app-table__sorted-cell' : undefined;

  const columns: ColumnsType<GetOrdersQuery['order'][number]> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Order #',
      sorter: true,
      sortOrder: getColumnSortOrder('id'),
      className: getSortedColumnClassName('id'),
      width: '5%',
    },
    {
      dataIndex: 'shortCode',
      key: 'shortCode',
      title: 'Short Code',
      sorter: true,
      sortOrder: getColumnSortOrder('shortCode'),
      className: getSortedColumnClassName('shortCode'),
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: 'Status',
      render: (_, record) => {
        return <Tag color="default">{record.status?.name}</Tag>;
      },
    },
    {
      dataIndex: 'deliverTo',
      key: 'deliverTo',
      title: 'Deliver to',
      width: '10%',
      ellipsis: true,
      render: (_, record) => {
        const addressParts = [
          record.client.address?.city,
          record.client.address?.district,
          record.client.address?.street,
        ].filter((part) => part?.trim());
        return addressParts.join(', ');
      },
    },
    {
      dataIndex: 'totalCents',
      key: 'totalCents',
      title: 'Total price',
      sorter: true,
      render: (totalCents, record) => {
        const renderPaymentType = record.paymentType ? (
          <Tag color={'blue'}> {record.paymentType[0].toUpperCase()}</Tag>
        ) : null;
        return (
          <>
            {renderPaymentType} {totalCents}
          </>
        );
      },
      sortOrder: getColumnSortOrder('totalCents'),
      className: getSortedColumnClassName('totalCents'),
      ...usePriceFilterProps(filters.priceRange),
    },
    {
      dataIndex: 'plannedDate',
      key: 'plannedDate',
      title: 'Planned date',
      sorter: true,
      sortOrder: getColumnSortOrder('plannedDate'),
      className: getSortedColumnClassName('plannedDate'),
      render: (field, record) => (
        <>
          {Boolean(record.rescheduleCount) && (
            <Tag color={'blue'}>RE: {record.rescheduleCount}</Tag>
          )}
          {field == null ? '' : dayjs(field).utc().format('MMM Do, h:mm a')}
        </>
      ),
      ...useDateFilterProps('plannedDate', filters.dates.plannedDate),
    },
    {
      dataIndex: 'deliveredDate',
      key: 'deliveredDate',
      title: 'Deliver date',
      sorter: true,
      sortOrder: getColumnSortOrder('deliveredDate'),
      className: getSortedColumnClassName('deliveredDate'),
      render: (field) =>
        field == null
          ? ''
          : dayjs(field)
              .utcOffset((selectedDepartment?.timeZone ?? 0) * 60)
              .format('MMM Do, h:mm a'),
      ...useDateFilterProps('deliveredDate', filters.dates.deliveredDate),
    },
    {
      dataIndex: 'firstPosition',
      key: 'firstPosition',
      width: '8%',
      title: '1st Position',
      filters: meta?.product?.map((product) => ({
        text: product.name,
        value: product.id,
      })),
      filteredValue: filters.products.length ? filters.products : null,
      render: (_, record) => record.positions[0]?.product?.name || '',
    },
    {
      dataIndex: 'resale',
      key: 'resale',
      title: 'Resale',
      align: 'center',
      width: '5%',
      filters: [
        { text: 'Yes', value: 'true' },
        { text: 'No', value: 'false' },
      ],
      filteredValue: filters.resale != null ? [String(filters.resale)] : null,
      render: (resale) => (resale ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      dataIndex: 'isSaved',
      key: 'isSaved',
      title: 'Saved',
      align: 'center',
      width: '5%',
      filters: [
        { text: 'Yes', value: 'true' },
        { text: 'No', value: 'false' },
      ],
      filteredValue: filters.isSaved != null ? [String(filters.isSaved)] : null,
      render: (isSaved) => (isSaved ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      dataIndex: 'driverID',
      key: 'driverID',
      title: 'DRV',
      filterDropdown: (props: FilterDropdownProps) => (
        <DriverFilterDropdown
          {...props}
          drivers={driverFilterList}
          driversLoading={filters.showBlockedDrivers && blockedDriversLoading}
          showBlockedDrivers={filters.showBlockedDrivers}
          onToggleBlockedDrivers={handleToggleBlockedDrivers}
        />
      ),
      filteredValue: filters.driverIDs?.length ? filters.driverIDs : null,
      render: (_, record) => {
        if (!record.driver) return '';
        return (
          <>
            {record.driver.name}
            {record.driver.blocked && (
              <Tag color="red" style={BLOCKED_TAG_STYLE}>
                B
              </Tag>
            )}
          </>
        );
      },
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      title: 'Updated at',
      sorter: true,
      sortOrder: getColumnSortOrder('updatedAt'),
      className: getSortedColumnClassName('updatedAt'),
      render: (field) =>
        field == null
          ? ''
          : dayjs(field)
              .utcOffset((selectedDepartment?.timeZone ?? 0) * 60)
              .format('MMM Do, h:mm a'),
      ...useDateFilterProps('updatedAt', filters.dates.updatedAt),
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: 'Created at',
      sorter: true,
      sortOrder: getColumnSortOrder('createdAt'),
      className: getSortedColumnClassName('createdAt'),
      render: (field) =>
        dayjs(field)
          .utcOffset((selectedDepartment?.timeZone ?? 0) * 60)
          .format('YYYY-MM-DD, HH:mm'),
      ...useDateFilterProps('createdAt', filters.dates.createdAt),
    },
  ];

  const currentData = useMemo(() => {
    const allData = orders?.order || [];
    if (allData.length === pagination.total) {
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      return allData.slice(startIndex, startIndex + pagination.pageSize);
    }
    return allData;
  }, [orders, pagination]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(400);
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setTableHeight(containerRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const tableSkeleton = useTableSkeleton(columns, currentData, ordersLoading, 15);

  return (
    <>
      <Content style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
        {statuses ? (
          <StatusFilterLinks
            onStatusChange={handleStatusChange}
            selectedStatus={statusFromUrl ?? filters.status}
            statusesData={statuses}
          />
        ) : (
          <StatusFilterSkeleton />
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Space style={{ padding: 8 }}>
            <Input.Search
              style={{ width: 300 }}
              placeholder="Search by name, phone or short code"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
            <Divider size={'middle'} type="vertical" />
            <Checkbox
              checked={filters.showOnlyExpress === true}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  showOnlyExpress: e.target.checked ? true : null,
                }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            >
              Show only express
            </Checkbox>
            <Divider size={'middle'} type="vertical" />
            <Button onClick={resetFilters}>Reset filters</Button>
          </Space>
          <div ref={containerRef} style={{ flex: 1 }}>
            <Table
              className="app-table"
              footer={() => {
                const total = orders?.order_aggregate?.aggregate?.count || 0;
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <UserEmail />
                    <Text style={{ marginLeft: 'auto', marginRight: 20 }}>Total: {total}</Text>
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      pageSizeOptions={[10, 15, 20, 50, 100, 1000]}
                      total={total}
                      onChange={(page, pageSize) =>
                        setPagination((prev) => ({ ...prev, current: page, pageSize }))
                      }
                      onShowSizeChange={(current, size) =>
                        setPagination((prev) => ({ ...prev, current, pageSize: size }))
                      }
                    />
                  </div>
                );
              }}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedOrders,
                getCheckboxProps: () => ({
                  disabled: tableSkeleton.isInitialLoad,
                }),
                onChange: (selectedRowKeys) => {
                  if (tableSkeleton.isInitialLoad) return;
                  const nextSelectedOrders = selectedRowKeys as number[];
                  setSelectedOrders(nextSelectedOrders);
                  persistSelectedOrders(nextSelectedOrders);
                  lastOrderIdFromUrlRef.current = null;
                  if (nextSelectedOrders.length === 1) {
                    setOrderIdParam(nextSelectedOrders[0]);
                  } else {
                    skipUrlOrderIdRef.current = orderIdParam ?? null;
                    setOrderIdParam(null);
                  }
                },
              }}
              style={{ overflowX: 'hidden' }}
              rowKey={(record) => record.id}
              dataSource={tableSkeleton.dataSource}
              pagination={false}
              columns={tableSkeleton.columns}
              loading={tableSkeleton.loading}
              size="small"
              onRow={(record) => ({
                onClick: () => {
                  if (tableSkeleton.isInitialLoad) return;
                  const nextSelectedOrders = [record.id];
                  setSelectedOrders(nextSelectedOrders);
                  persistSelectedOrders(nextSelectedOrders);
                  lastOrderIdFromUrlRef.current = null;
                  setOrderIdParam(record.id);
                },
              })}
              rowClassName={(record) => {
                const expressStatuses = [
                  'NEW',
                  'DRIVERASSIGNED',
                  'DRIVERCONFIRMED',
                  'DRIVERONTHEWAY',
                ];
                const statusName = record.status?.name?.toUpperCase() || '';
                const isExpressStatus = expressStatuses.includes(statusName);
                const isExpressProvider = record.delivery_provider?.short_name === 'DTK_EXPRESS';
                const isSelected = selectedOrders.includes(record.id);
                const classes: string[] = [];
                if (isExpressStatus && isExpressProvider) {
                  classes.push('row-express');
                }
                if (isSelected) {
                  classes.push('row-selected');
                }
                return classes.join(' ');
              }}
              onChange={handleTableChange}
              scroll={{ y: tableHeight - 96 }}
            />
          </div>
        </div>
      </Content>
      <Sider
        width="460px"
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          overflowY: 'auto',
          height: 'calc(100vh - 32px)',
          boxShadow: '0 0 3px #0002',
          position: 'absolute',
          right: 0,
          top: 32,
          zIndex: 1000,
        }}
        className="order-drawer"
        hidden={!selectedOrders.length}
      >
        <OrderDrawerView selectedOrders={selectedOrders} close={closeDrawer} />
      </Sider>
    </>
  );
};

interface DriverFilterDropdownProps extends FilterDropdownProps {
  drivers: Array<{ id: string; name: string }> | undefined;
  driversLoading: boolean;
  showBlockedDrivers: boolean;
  onToggleBlockedDrivers: (checked: boolean) => void;
}

const DRIVER_FILTER_DROPDOWN_STYLE: React.CSSProperties = { padding: 8, width: 250 };
const DRIVER_TOGGLE_ROW_STYLE: React.CSSProperties = {
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const DRIVER_LIST_STYLE: React.CSSProperties = {
  maxHeight: 250,
  overflow: 'auto',
  marginBottom: 8,
};
const DRIVER_ITEM_STYLE: React.CSSProperties = { padding: '4px 0' };
const DRIVER_EMPTY_STYLE: React.CSSProperties = {
  color: '#999',
  textAlign: 'center',
  padding: 8,
};
const DRIVER_ACTIONS_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};

const DriverFilterDropdown: FC<DriverFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  drivers,
  driversLoading,
  showBlockedDrivers,
  onToggleBlockedDrivers,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDrivers = useMemo(() => {
    if (!drivers) return [];
    if (!searchQuery.trim()) return drivers;
    const query = searchQuery.trim().toLowerCase();
    return drivers.filter((d) => d.name.toLowerCase().includes(query));
  }, [drivers, searchQuery]);

  const stringKeys = useMemo(() => selectedKeys.map(String), [selectedKeys]);
  const selectedSet = useMemo(() => new Set(stringKeys), [stringKeys]);

  const handleCheck = useCallback(
    (driverId: string, checked: boolean) => {
      const next = checked ? [...stringKeys, driverId] : stringKeys.filter((k) => k !== driverId);
      setSelectedKeys(next);
    },
    [stringKeys, setSelectedKeys]
  );

  const handleReset = useCallback(() => {
    setSelectedKeys([]);
    setSearchQuery('');
    if (clearFilters) clearFilters();
    confirm({ closeDropdown: false });
  }, [clearFilters, confirm, setSelectedKeys]);

  const handleToggle = useCallback(
    (checked: boolean) => {
      setSelectedKeys([]);
      setSearchQuery('');
      if (clearFilters) clearFilters();
      onToggleBlockedDrivers(checked);
      confirm({ closeDropdown: false });
    },
    [clearFilters, confirm, onToggleBlockedDrivers, setSelectedKeys]
  );

  return (
    <div style={DRIVER_FILTER_DROPDOWN_STYLE}>
      <div style={DRIVER_TOGGLE_ROW_STYLE}>
        <Switch
          size="small"
          checked={showBlockedDrivers}
          onChange={handleToggle}
          aria-label="Blocked drivers"
        />
        <span>Blocked drivers</span>
      </div>
      <Input
        placeholder="Search driver"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        allowClear
        size="small"
        style={{ marginBottom: 8 }}
      />
      <div style={DRIVER_LIST_STYLE}>
        {driversLoading ? (
          <div style={DRIVER_EMPTY_STYLE}>
            <Spin size="small" />
          </div>
        ) : (
          <>
            {filteredDrivers.map((driver) => (
              <div key={driver.id} style={DRIVER_ITEM_STYLE}>
                <Checkbox
                  checked={selectedSet.has(driver.id)}
                  onChange={(e) => handleCheck(driver.id, e.target.checked)}
                >
                  {driver.name}
                </Checkbox>
              </div>
            ))}
            {filteredDrivers.length === 0 && <div style={DRIVER_EMPTY_STYLE}>No drivers found</div>}
          </>
        )}
      </div>
      <div style={DRIVER_ACTIONS_STYLE}>
        <Button type="link" size="small" onClick={handleReset}>
          Reset
        </Button>
        <Button type="primary" size="small" onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  );
};

interface PriceFilterDropdownProps extends FilterDropdownProps {
  dataIndex: string;
}

const PriceFilterDropdown: FC<PriceFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const parseSelectedKeys = useCallback((): [number | null, number | null] => {
    if (selectedKeys?.[0]) {
      const [minStr, maxStr] = String(selectedKeys[0]).split('|');
      return [
        minStr && minStr !== '' && !Number.isNaN(Number(minStr)) ? Number(minStr) : null,
        maxStr && maxStr !== '' && !Number.isNaN(Number(maxStr)) ? Number(maxStr) : null,
      ];
    }
    return [null, null];
  }, [selectedKeys]);

  const [priceRange, setPriceRange] = useState<[number | null, number | null]>(parseSelectedKeys());

  useEffect(() => {
    setPriceRange(parseSelectedKeys());
  }, [parseSelectedKeys]);

  const handleReset = useCallback(() => {
    setPriceRange([null, null]);
    setSelectedKeys([]);
    if (clearFilters) clearFilters();
    confirm({ closeDropdown: false });
  }, [clearFilters, confirm, setSelectedKeys]);

  const handleFilter = useCallback(() => {
    const [min, max] = priceRange;
    if (min === null && max === null) {
      if (clearFilters) clearFilters();
      setSelectedKeys([]);
    } else {
      const minStr = min !== null ? String(min) : '';
      const maxStr = max !== null ? String(max) : '';
      setSelectedKeys([`${minStr}|${maxStr}`]);
    }
    confirm();
  }, [clearFilters, confirm, priceRange, setSelectedKeys]);

  return (
    <div style={{ padding: 8 }}>
      <Space orientation="vertical" style={{ width: '100%' }}>
        <InputNumber
          placeholder="Min price"
          value={priceRange[0]}
          onChange={(value) => setPriceRange([value, priceRange[1]])}
          min={0}
          precision={0}
          style={{ width: '100%' }}
        />
        <InputNumber
          placeholder="Max price"
          value={priceRange[1]}
          onChange={(value) => setPriceRange([priceRange[0], value])}
          min={0}
          precision={0}
          style={{ width: '100%' }}
        />
      </Space>
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Button type="link" size="small" onClick={handleReset}>
          Reset
        </Button>
        <Button type="primary" size="small" onClick={handleFilter}>
          Filter
        </Button>
      </div>
    </div>
  );
};

interface DateFilterDropdownProps extends FilterDropdownProps {
  dataIndex: string;
}

const DateFilterDropdown: FC<DateFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const parseSelectedKeys = useCallback((): [Dayjs | null, Dayjs | null] => {
    if (selectedKeys?.[0]) {
      const [startStr, endStr] = String(selectedKeys[0]).split('|');
      return [dayjs.utc(startStr), dayjs.utc(endStr)];
    }
    return [null, null];
  }, [selectedKeys]);

  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>(parseSelectedKeys());

  useEffect(() => {
    setDates(parseSelectedKeys());
  }, [parseSelectedKeys]);

  const handleReset = useCallback(() => {
    setDates([null, null]);
    setSelectedKeys([]);
    if (clearFilters) clearFilters();
    confirm({ closeDropdown: false });
  }, [clearFilters, confirm, setSelectedKeys]);

  const handleFilter = useCallback(() => {
    if (!dates?.[0] || !dates[1]) {
      if (clearFilters) clearFilters();
      setSelectedKeys([]);
    } else {
      const startDate = dayjs.utc(`${dates[0].format('YYYY-MM-DD')}T00:00:00`).toISOString();
      const endDate = dayjs.utc(`${dates[1].format('YYYY-MM-DD')}T23:59:59.999`).toISOString();
      setSelectedKeys([`${startDate}|${endDate}`]);
    }
    confirm();
  }, [clearFilters, confirm, dates, setSelectedKeys]);

  return (
    <div style={{ padding: 8 }}>
      <DatePicker.RangePicker
        value={dates}
        onChange={(newDates) => setDates(newDates as [Dayjs | null, Dayjs | null])}
        style={{ marginBottom: 8 }}
        format="YYYY-MM-DD"
      />
      <div style={{ textAlign: 'right' }}>
        <Button type="link" size="small" onClick={handleReset}>
          Reset
        </Button>
        <Button type="primary" size="small" onClick={handleFilter}>
          Filter
        </Button>
      </div>
    </div>
  );
};

function useDateFilterProps(
  dataIndex: 'plannedDate' | 'deliveredDate' | 'updatedAt' | 'createdAt',
  selectedDateRange: [string, string] | null
): Partial<ColumnsType<GetOrdersQuery['order'][number]>[number]> {
  return useMemo(() => {
    return {
      filterDropdown: (props: FilterDropdownProps) => (
        <DateFilterDropdown {...props} dataIndex={dataIndex} />
      ),
      filteredValue: selectedDateRange ? [`${selectedDateRange[0]}|${selectedDateRange[1]}`] : null,
    };
  }, [selectedDateRange, dataIndex]);
}

function usePriceFilterProps(
  selectedPriceRange: [string, string] | null
): Partial<ColumnsType<GetOrdersQuery['order'][number]>[number]> {
  return useMemo(() => {
    return {
      filterDropdown: (props: FilterDropdownProps) => (
        <PriceFilterDropdown {...props} dataIndex="totalCents" />
      ),
      filteredValue: selectedPriceRange
        ? [`${selectedPriceRange[0]}|${selectedPriceRange[1]}`]
        : null,
    };
  }, [selectedPriceRange]);
}
